import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import isEmpty from "lodash/isEmpty";
import { LIVE_TYPES } from "../../helpers/constant";
import { ILiveState, ISearchLivesPayload, IStreamChallengeRequest } from "../../helpers/types";
import { liveService } from "../../services";

const initialState: ILiveState = {
  list: [],
  todayScheduleList: [],
  upcomingScheduleList: [],
  rtmpLive: null,
  rtmpLiveLoading: false,
  discoverList: [],
  suggestedList: [],
  item: null,
  token: null,
  loading: false,
  error: null,
  isNewLive: false,
};

export const publishLive = createAsyncThunk("lives/publishLive", async (data: FormData) => {
  return liveService.publishLive(data);
});

export const getLiveById = createAsyncThunk("lives/getLiveById", async (id: string) => {
  return liveService.getLiveById(id);
});

export const buildToken = createAsyncThunk("lives/buildToken", async (sessionId: string) => {
  return liveService.buildToken(sessionId);
});

export const getLivesForFan = createAsyncThunk("lives/getLivesForFan", async (params: ISearchLivesPayload = { startType: LIVE_TYPES.NOW }) => {
  return liveService.getLivesForFan(params);
});

export const getPublicScheduleLive = createAsyncThunk("lives/getPublicScheduleLive", async () => {
  return liveService.getPublicScheduleLive();
});

// export const join = createAsyncThunk("lives/join", async (sessionId: string) => {
//   return liveService.join(sessionId);
// });

export const getCreatorScheduleLive = createAsyncThunk("lives/creator/schedule", async (id: string) => {
  return liveService.getCreatorScheduleLive(id);
});

export const getReservations = createAsyncThunk("lives/getReservations", async (id: string) => {
  return liveService.getReservationsCount(id);
});

export const getTotalPrice = createAsyncThunk("lives/getTotalPrice", async (id: string) => {
  return liveService.getTotalPrice(id);
});

export const deleteLive = createAsyncThunk("lives/delete", async (id: string) => {
  return liveService.deleteLive(id);
});

export const getRtmpLive = createAsyncThunk("lives/getRtmpLive", async () => {
  return liveService.getRtmpLive();
});

export const createStreamChallenge = createAsyncThunk("lives/createStreamChallenge", async (payload: IStreamChallengeRequest) => {
  return liveService.createChallenge(payload);
});

export const deleteChallenge = createAsyncThunk("lives/deleteChallenge", async (id: string) => {
  return liveService.deleteChallenge(id);
});

// export const updateStatusChallenge = createAsyncThunk("lives/updateStatusChallenge", async (id: string) => {
//   return liveService.updateStatusChallenge(id);
// });

const livesSlice = createSlice({
  name: "lives",
  initialState,
  reducers: {
    setActiveLives: (state, action) => {
      if (action.payload.startType === LIVE_TYPES.NOW) {
        state.discoverList = action.payload.data.discover;
        state.suggestedList = action.payload.data.suggested;
      }
      if (action.payload.data.rtmps.length > 0) {
        state.rtmpLive = action.payload.data.rtmps[0];
      }
    },
    editLive: (state, action) => {
      state.item = action.payload;
    },
    updateLives: (state, action) => {
      state.list = action.payload;
    },
    newLivePublished: (state, action) => {
      state.isNewLive = action.payload;
    },
    updateEndLive: (state, action) => {
      const lives = [...state.discoverList, ...state.suggestedList];
      const liveToEdit = lives.find((item) => item._id === action.payload.streamId);
      if (liveToEdit) {
        if (action.payload.isStreamingEnds) {
          if (liveToEdit.isSuggested) {
            state.suggestedList = state.suggestedList.filter((live) => live._id !== action.payload.streamId);
          } else {
            state.discoverList = state.discoverList.filter((live) => live._id !== action.payload.streamId);
          }
        }
      }
      return state;
    },
    // followCreator: (state, action) => {
    //   const newLives =
    //     state.list &&
    //     state.list.map((live) => {
    //       if (live.creator?._id === action.payload.id) {
    //         live.isFollowed = action.payload.isFollowed;
    //       }
    //       return live;
    //     });
    //   state.list = newLives;
    // },
    // unfollowCreator: (state, action) => {
    //   const newLives =
    //     state.list &&
    //     state.list.map((live) => {
    //       if (live.creator?._id === action.payload.id) {
    //         live.isFollowed = action.payload.isFollowed;
    //       }
    //       return live;
    //     });
    //   state.list = newLives;
    // },
    clearLiveData: (state) => {
      state.item = null;
      state.token = null;
    },
    clearToken: (state) => {
      state.token = null;
    },
    updateLivePayment: (state, action) => {
      const liveListToEdit = action.payload.isSuggested ? "suggestedList" : "discoverList";
      if (state[liveListToEdit]) {
        const liveIndex = state[liveListToEdit].findIndex((live) => live._id === action.payload.id);
        if (liveIndex !== -1) {
          state[liveListToEdit][liveIndex].isPaymentVerified = true;
        }
      }
    },
    updateRTMPLivePayment: (state) => {
      if (state.rtmpLive) {
        state.rtmpLive.isPaymentVerified = true;
      }
    },
    setRTMPStream: (state, action) => {
      if (!state.rtmpLive) {
        state.rtmpLive = action.payload;
      }
    },
    endSuccessChallenge: (state) => {
      if (state.item !== null) {
        state.item.currentChallenge = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(buildToken.pending, (state, action) => {
      state.token = null;
      state.loading = true;
      state.error = null;
    });
    builder.addCase(buildToken.fulfilled, (state, action: any) => {
      state.token = action.payload;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(buildToken.rejected, (state, action) => {
      state.token = null;
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(getLivesForFan.pending, (state, action) => {
      state.list = [];
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getLivesForFan.fulfilled, (state, action: any) => {
      if (action.payload.startType === LIVE_TYPES.SCHEDULE) {
        state.todayScheduleList = action.payload.data.today;
        state.upcomingScheduleList = action.payload.data.upcoming;
      } else {
        // state.list = action.payload.data;
        state.discoverList = action.payload.data.discover;
        state.suggestedList = action.payload.data.suggested;
      }
      if (action.payload.data.rtmps.length > 0) {
        state.rtmpLive = action.payload.data.rtmps[0];
      }
      state.loading = false;
      state.error = null;
    });
    builder.addCase(getLivesForFan.rejected, (state, action) => {
      state.list = [];
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(getPublicScheduleLive.pending, (state, action) => {
      state.list = [];
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getPublicScheduleLive.fulfilled, (state, action: any) => {
      state.todayScheduleList = action.payload.today;
      state.upcomingScheduleList = action.payload.upcoming;
      if (action.payload.rtmps && action.payload.rtmps.length > 0) {
        state.rtmpLive = action.payload.rtmps[0];
      }
      state.loading = false;
      state.error = null;
    });
    builder.addCase(getPublicScheduleLive.rejected, (state, action) => {
      state.list = [];
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(getReservations.pending, (state, action) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getReservations.fulfilled, (state, action: any) => {
      state.loading = false;
      state.item = { ...state.item, reservations: action.payload };
    });
    builder.addCase(getReservations.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(getTotalPrice.fulfilled, (state, action: any) => {
      state.item = { ...state.item, totalPrice: action.payload };
    });
    builder.addCase(deleteLive.pending, (state, action) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteLive.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(deleteLive.fulfilled, (state, action) => {
      state.loading = false;
      state.item = null;
      state.error = null;
    });

    builder.addCase(getRtmpLive.pending, (state, action) => {
      state.rtmpLiveLoading = true;
      state.error = null;
    });
    builder.addCase(getRtmpLive.fulfilled, (state, action: any) => {
      state.rtmpLiveLoading = false;
      if (!isEmpty(action.payload)) {
        state.rtmpLive = action.payload;
      }
    });
    builder.addCase(getRtmpLive.rejected, (state, action) => {
      state.rtmpLiveLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(createStreamChallenge.pending, (state, action) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createStreamChallenge.fulfilled, (state, action: any) => {
      if (state.item !== null) {
        state.item.currentChallenge = action.payload;
      }
      state.loading = false;
      state.error = null;
    });
    builder.addCase(createStreamChallenge.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(deleteChallenge.pending, (state, action) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteChallenge.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(deleteChallenge.fulfilled, (state, action) => {
      if (state.item !== null) {
        state.item.currentChallenge = null;
      }
      state.loading = false;
      state.error = null;
    });
    // builder.addCase(updateStatusChallenge.pending, (state, action) => {
    //   state.loading = true;
    //   state.error = null;
    // });
    // builder.addCase(updateStatusChallenge.rejected, (state, action) => {
    //   state.loading = false;
    //   state.error = action.error.message || "Something went wrong";
    // });
    // builder.addCase(updateStatusChallenge.fulfilled, (state, action) => {
    //   if (state.item !== null) {
    //     state.item.currentChallenge = null;
    //   }
    //   state.loading = false;
    //   state.error = null;
    // });
    builder.addMatcher(isAnyOf(publishLive.pending, getLiveById.pending, getCreatorScheduleLive.pending), (state, action) => {
      state.item = null;
      state.loading = true;
      state.error = null;
    });
    builder.addMatcher(isAnyOf(publishLive.fulfilled, getLiveById.fulfilled, getCreatorScheduleLive.fulfilled), (state, action) => {
      state.item = action.payload;
      state.loading = false;
      state.error = null;
    });
    builder.addMatcher(isAnyOf(publishLive.rejected, getLiveById.rejected, getCreatorScheduleLive.rejected), (state, action) => {
      state.item = null;
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export const {
  setActiveLives,
  editLive,
  updateLives,
  updateEndLive,
  clearToken,
  clearLiveData,
  updateLivePayment,
  endSuccessChallenge,
  newLivePublished,
  updateRTMPLivePayment,
  setRTMPStream,
} = livesSlice.actions;

export default livesSlice.reducer;
